/**
 * The countdown disc (UX spec §7: scale 1.2 -> 1 over 600 ms per digit).
 *
 * A SIBLING of the board inside the frame, absolutely positioned — the board's
 * DOM must never gain or lose a parent (UX spec §6a).
 */
import React, {useEffect, useRef} from 'react';
import {Animated, Easing, Platform, StyleSheet} from 'react-native';

import {RADIUS, SIZE, TYPE} from '../../theme/tokens';
import {useV2Theme} from '../../theme/useV2Theme';
import {BoardCountdownModel} from './model';

const POP_FROM = 1.2;
const POP_MS = 600;
const DISC_OPACITY = 0.94;

export default React.memo(function BoardCountdownView({
  digit,
}: BoardCountdownModel) {
  const tokens = useV2Theme(s => s.tokens);
  const scale = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    if (digit <= 0) {
      return;
    }
    scale.setValue(POP_FROM);
    Animated.timing(scale, {
      toValue: 1,
      duration: POP_MS,
      easing: Easing.bezier(0.2, 0.8, 0.3, 1),
      // RNW cannot drive `transform` off the JS thread; native can.
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  }, [digit, scale]);

  if (digit <= 0) {
    return null;
  }

  return (
    <Animated.View
      pointerEvents="none"
      style={[
        styles.overlay,
        {backgroundColor: tokens.frame, transform: [{scale}]},
      ]}>
      {/* an Animated.Text, so the digit scales with the disc */}
      <Animated.Text style={[styles.digit, {color: tokens.ink}]}>
        {digit}
      </Animated.Text>
    </Animated.View>
  );
});

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: SIZE.countdown,
    height: SIZE.countdown,
    marginTop: -SIZE.countdown / 2,
    marginLeft: -SIZE.countdown / 2,
    borderRadius: RADIUS.card,
    alignItems: 'center',
    justifyContent: 'center',
    opacity: DISC_OPACITY,
  },
  digit: {
    ...TYPE.boardDigit,
    fontVariant: ['tabular-nums'],
  },
});
